"use client";

import {
  BadgeCheck,
  Lightbulb,
  ScanSearch,
  ThumbsUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useToggleReaction } from "@/hooks/use-feed";
import { REACTION_OPTIONS, type ReactionType } from "@/lib/feed/types";
import { cn } from "@/lib/utils";

const REACTION_ICONS = {
  like: ThumbsUp,
  useful: Lightbulb,
  interesting: ScanSearch,
  agree: BadgeCheck,
} as const;

type ReactionBarProps = {
  postId: string;
  counts: Record<ReactionType, number>;
  myReaction: ReactionType | null;
  currentUserId: string | null;
};

export function ReactionBar({
  postId,
  counts,
  myReaction,
  currentUserId,
}: ReactionBarProps) {
  const toggleReaction = useToggleReaction(postId);

  return (
    <div className="flex flex-wrap gap-1">
      {REACTION_OPTIONS.map((option) => {
        const Icon = REACTION_ICONS[option.type];
        const isActive = myReaction === option.type;
        const count = counts[option.type];

        return (
          <Button
            key={option.type}
            type="button"
            variant="ghost"
            size="sm"
            disabled={!currentUserId || toggleReaction.isPending}
            onClick={() => {
              if (!currentUserId) {
                return;
              }

              toggleReaction.mutate({
                userId: currentUserId,
                current: myReaction,
                next: option.type,
              });
            }}
            className={cn(
              "h-8 gap-1.5 px-2.5 text-xs text-muted-foreground max-md:size-8 max-md:px-0",
              isActive && "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary",
            )}
            aria-pressed={isActive}
            aria-label={`${option.label}${count > 0 ? `: ${count}` : ""}`}
          >
            <Icon className="size-3.5" />
            <span className="max-md:hidden">{option.label}</span>
            {count > 0 ? <span className="tabular-nums">{count}</span> : null}
          </Button>
        );
      })}
    </div>
  );
}
